import {
  useState,
  useMemo,
  useCallback,
  useRef,
  useLayoutEffect,
  useEffect,
  type FC,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Input, Typography, Tag, Empty, type InputRef } from 'antd';
import { SearchOutlined, FileTextOutlined, ProjectOutlined } from '@ant-design/icons';
import { useBlogPosts, useProjects } from '@/context/ContentProvider';
import { searchContent } from '@/utils/search';
import { trackSearchQuery } from '@/utils/analytics';

const { Text } = Typography;

export const SearchInput: FC = () => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const navigate = useNavigate();
  const { blogPosts } = useBlogPosts();
  const { projects } = useProjects();

  const results = useMemo(
    () => searchContent(query, blogPosts, projects),
    [query, blogPosts, projects],
  );

  const handleSearch = useCallback((value: string) => {
    setQuery(value);
    setOpen(value.length > 0);
    if (value.length > 2) {
      trackSearchQuery(value);
    }
  }, []);

  const handleSelect = useCallback((url: string) => {
    inputRef.current?.blur();
    navigate(url);
    setQuery('');
    setOpen(false);
  }, [navigate]);

  const showDropdown = open && query.length > 0;

  // Align dropdown right edge to the navbar pill's right edge so it
  // doesn't run off into empty viewport space past the floating pill.
  // Recomputed on open + window resize. (Scroll doesn't move the pill
  // since it's position:fixed.)
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    right: number;
  }>({ top: 82, right: 16 });

  const computePosition = useCallback(() => {
    const pill = document.querySelector('.navbar-pill');
    if (!pill) return;
    const rect = pill.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 14,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, []);

  useLayoutEffect(() => {
    if (!showDropdown) return;
    computePosition();
  }, [showDropdown, computePosition]);

  useEffect(() => {
    if (!showDropdown) return;
    window.addEventListener('resize', computePosition);
    return () => window.removeEventListener('resize', computePosition);
  }, [showDropdown, computePosition]);

  return (
    <div style={{ position: 'relative' }}>
      <Input
        ref={inputRef}
        prefix={<SearchOutlined style={{ color: 'var(--color-text-muted)' }} />}
        placeholder="Search..."
        value={query}
        onChange={(e) => handleSearch(e.target.value)}
        onFocus={() => query.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        style={{ width: window.innerWidth <= 768 ? 140 : 180, fontSize: 16 }}
        size="small"
        allowClear
      />

      {showDropdown && createPortal(
        <div
          className="search-dropdown"
          // Portaled to document.body so `backdrop-filter` can see the
          // actual page backdrop. If the dropdown stays nested inside
          // .navbar-pill (which has `isolation: isolate` + its own
          // backdrop-filter), the child's filter ends up applied to the
          // pill's already-filtered surface and reads as no blur at all.
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            right: dropdownPos.right,
            left: window.innerWidth <= 768 ? 16 : 'auto',
            width: window.innerWidth <= 768 ? undefined : 420,
            zIndex: 1050,
            maxHeight: 440,
          }}
        >
          {results.length === 0 ? (
            <Empty
              description="No results found"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: 16 }}
            />
          ) : (
            results.map((result) => (
              <div
                key={`${result.type}-${result.id}`}
                className="search-result-item"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(result.url);
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 4,
                  }}
                >
                  {result.type === 'blog' ? (
                    <FileTextOutlined style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                  ) : (
                    <ProjectOutlined style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                  )}
                  <Text strong style={{ fontSize: 14, flex: 1, minWidth: 0 }} ellipsis>
                    {result.title}
                  </Text>
                  <Tag
                    style={{ marginLeft: 'auto', fontSize: 11, flexShrink: 0 }}
                  >
                    {result.type}
                  </Tag>
                </div>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: 'block', paddingLeft: 22 }}
                >
                  {result.description.slice(0, 80)}...
                </Text>
              </div>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};
